import React, { useState } from 'react';
import { useShop } from '../../context/ShopContext';
import { formatINR } from '../../data/products';
import { 
  CreditCard, 
  Receipt, 
  FileSpreadsheet, 
  Download, 
  CheckCircle2, 
  Clock, 
  AlertCircle, 
  Search, 
  Calendar, 
  QrCode, 
  FileText, 
  DollarSign,
  TrendingUp,
  Percent,
  ShieldCheck
} from 'lucide-react';

export default function AdminPaymentsGST() {
  const { orders, addToast, setInvoiceModalOrder } = useShop();

  const [activeTab, setActiveTab] = useState('transactions'); // 'transactions', 'gst-reports', 'hsn-config'
  const [searchTerm, setSearchTerm] = useState('');
  const [paymentFilter, setPaymentFilter] = useState('all');

  // Derive transactions from orders
  const transactions = orders.map((ord, idx) => ({
    id: `TXN-${10000 + idx + 1}`,
    orderId: ord.id,
    customerName: ord.customer?.name || 'Walk-in Customer',
    amount: ord.total || 0,
    metalValue: Math.round((ord.total || 0) * 0.88),
    makingCharges: Math.round((ord.total || 0) * 0.09),
    gstAmount: Math.round((ord.total || 0) * 0.03),
    method: ord.paymentMethod || (idx % 2 === 0 ? 'UPI / PhonePe' : 'Razorpay Gateway'),
    status: ord.status === 'Cancelled' ? 'Refunded' : 'Captured',
    date: ord.date || new Date().toISOString().slice(0, 10),
    hsnCode: '7113'
  }));

  const totalCaptured = transactions
    .filter(t => t.status === 'Captured')
    .reduce((sum, t) => sum + t.amount, 0);

  const totalGSTCollected = transactions
    .filter(t => t.status === 'Captured')
    .reduce((sum, t) => sum + t.gstAmount, 0);

  const handleExportGSTR1 = () => {
    const headers = ['Invoice No,Date,Customer Name,HSN Code,Taxable Value,CGST (1.5%),SGST (1.5%),Total GST (3%),Invoice Total,Payment Mode'];
    const rows = transactions.map(t => 
      `"${t.orderId}","${t.date}","${t.customerName}","${t.hsnCode}","${t.metalValue + t.makingCharges}","${Math.round(t.gstAmount / 2)}","${Math.round(t.gstAmount / 2)}","${t.gstAmount}","${t.amount}","${t.method}"`
    );
    const csvContent = 'data:text/csv;charset=utf-8,' + [headers, ...rows].join('\n');
    const encodedUri = encodeURI(csvContent);
    const link = document.createElement('a');
    link.setAttribute('href', encodedUri);
    link.setAttribute('download', `GSTR1_Jewellery_Sales_${new Date().toISOString().slice(0, 10)}.csv`);
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    addToast?.('GSTR-1 Tax report exported for CA filing', 'success');
  };

  return (
    <div style={{ maxWidth: '1240px', margin: '0 auto' }}>
      
      {/* Top Banner */}
      <div style={{
        background: '#ffffff',
        borderRadius: '8px',
        padding: '1.25rem 1.6rem',
        border: '1px solid #e0e0e0',
        marginBottom: '1.5rem',
        display: 'flex',
        flexWrap: 'wrap',
        alignItems: 'center',
        justifyContent: 'space-between',
        gap: '1rem',
        boxShadow: '0 1px 3px rgba(0,0,0,0.05)'
      }}>
        <div>
          <div style={{ display: 'inline-flex', alignItems: 'center', gap: '0.4rem', color: '#2874f0', fontSize: '0.72rem', fontWeight: 800, textTransform: 'uppercase', letterSpacing: '0.6px', marginBottom: '0.2rem' }}>
            <Receipt size={14} />
            <span>STATUTORY COMPLIANCE & RECONCILIATION</span>
          </div>
          <h2 style={{ fontSize: '1.45rem', fontWeight: 800, color: '#212121', margin: 0 }}>
            Payments & Statutory GST Invoices
          </h2>
          <p style={{ margin: '0.2rem 0 0', fontSize: '0.82rem', color: '#616161' }}>
            HSN 7113 Tax filings, Razorpay/UPI gateway reconciliation, CGST/SGST ledger & CA export files.
          </p>
        </div>

        <div style={{ display: 'flex', gap: '0.6rem', flexWrap: 'wrap' }}>
          <button
            onClick={handleExportGSTR1}
            style={{
              display: 'inline-flex',
              alignItems: 'center',
              gap: '0.4rem',
              padding: '0.55rem 1rem',
              borderRadius: '4px',
              border: 'none',
              background: '#15803d',
              color: '#ffffff',
              fontWeight: 700,
              fontSize: '0.82rem',
              cursor: 'pointer'
            }}
          >
            <Download size={15} />
            <span>Export GSTR-1 (CSV)</span>
          </button>
        </div>
      </div>

      {/* KPI Cards */}
      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(220px, 1fr))', gap: '1rem', marginBottom: '1.5rem' }}>
        {[
          { label: 'TOTAL REVENUE SETTLED', val: formatINR(totalCaptured), sub: 'Gateway + UPI QR + Showroom', color: '#15803d' },
          { label: 'STATUTORY GST COLLECTED', val: formatINR(totalGSTCollected), sub: '3% Bullion / Jewellery Tax', color: '#2874f0' },
          { label: 'CGST (1.5%) LIABILITIES', val: formatINR(Math.round(totalGSTCollected / 2)), sub: 'Central Goods & Services Tax', color: '#7c3aed' },
          { label: 'SGST (1.5%) LIABILITIES', val: formatINR(Math.round(totalGSTCollected / 2)), sub: 'Rajasthan State GST Reserve', color: '#b45309' }
        ].map(k => (
          <div key={k.label} style={{ background: '#ffffff', padding: '1.1rem 1.3rem', borderRadius: '8px', border: '1px solid #e0e0e0', boxShadow: '0 1px 3px rgba(0,0,0,0.04)' }}>
            <div style={{ fontSize: '0.7rem', fontWeight: 800, color: '#757575', letterSpacing: '0.5px' }}>{k.label}</div>
            <div style={{ fontSize: '1.4rem', fontWeight: 800, color: k.color, margin: '0.3rem 0' }}>{k.val}</div>
            <div style={{ fontSize: '0.72rem', color: '#9e9e9e' }}>{k.sub}</div>
          </div>
        ))}
      </div>

      {/* Tabs */}
      <div style={{ display: 'flex', gap: '0.5rem', marginBottom: '1.25rem', borderBottom: '1px solid #e0e0e0', paddingBottom: '0.5rem' }}>
        {[
          { id: 'transactions', label: 'Payment Transactions Log', icon: <CreditCard size={15} /> },
          { id: 'gst-reports', label: 'GST Monthly Summary (HSN 7113)', icon: <Receipt size={15} /> },
          { id: 'hsn-config', label: 'Tax Rates & HSN Configuration', icon: <Percent size={15} /> }
        ].map(t => (
          <button
            key={t.id}
            onClick={() => setActiveTab(t.id)}
            style={{
              display: 'flex',
              alignItems: 'center',
              gap: '0.45rem',
              padding: '0.55rem 0.9rem',
              borderRadius: '4px',
              border: 'none',
              background: activeTab === t.id ? '#e8f0fe' : 'transparent',
              color: activeTab === t.id ? '#2874f0' : '#424242',
              fontWeight: activeTab === t.id ? 700 : 500,
              fontSize: '0.82rem',
              cursor: 'pointer'
            }}
          >
            {t.icon}
            <span>{t.label}</span>
          </button>
        ))}
      </div>

      {/* Tab 1: Transactions Log */}
      {activeTab === 'transactions' && (
        <div style={{ background: '#ffffff', borderRadius: '8px', border: '1px solid #e0e0e0', overflow: 'hidden' }}>
          <div style={{ padding: '0.9rem 1.2rem', borderBottom: '1px solid #f0f0f0', display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
            <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem', flex: 1, maxWidth: '350px' }}>
              <Search size={15} color="#757575" />
              <input
                type="text"
                placeholder="Search transaction ID, customer or order..."
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                style={{ width: '100%', border: 'none', outline: 'none', fontSize: '0.85rem' }}
              />
            </div>
            <div style={{ display: 'flex', gap: '0.4rem' }}>
              {['all', 'UPI / PhonePe', 'Razorpay Gateway', 'Showroom Cash'].map(m => (
                <button
                  key={m}
                  onClick={() => setPaymentFilter(m)}
                  style={{
                    padding: '0.3rem 0.6rem',
                    fontSize: '0.72rem',
                    borderRadius: '3px',
                    border: '1px solid #d0d7de',
                    background: paymentFilter === m ? '#2874f0' : '#fff',
                    color: paymentFilter === m ? '#fff' : '#333',
                    cursor: 'pointer',
                    fontWeight: 600
                  }}
                >
                  {m === 'all' ? 'All Channels' : m}
                </button>
              ))}
            </div>
          </div>

          <table style={{ width: '100%', borderCollapse: 'collapse', textAlign: 'left', fontSize: '0.82rem' }}>
            <thead>
              <tr style={{ background: '#f8fafc', borderBottom: '1px solid #e2e8f0', color: '#475569', fontSize: '0.74rem', textTransform: 'uppercase' }}>
                <th style={{ padding: '0.8rem 1rem' }}>Txn / Order ID</th>
                <th style={{ padding: '0.8rem 1rem' }}>Client</th>
                <th style={{ padding: '0.8rem 1rem' }}>Payment Channel</th>
                <th style={{ padding: '0.8rem 1rem' }}>Total Amount</th>
                <th style={{ padding: '0.8rem 1rem' }}>GST (3%)</th>
                <th style={{ padding: '0.8rem 1rem' }}>Gateway Status</th>
                <th style={{ padding: '0.8rem 1rem', textAlign: 'right' }}>Tax Invoice</th>
              </tr>
            </thead>
            <tbody>
              {transactions
                .filter(t => paymentFilter === 'all' || t.method.includes(paymentFilter))
                .filter(t => t.orderId.toLowerCase().includes(searchTerm.toLowerCase()) || t.customerName.toLowerCase().includes(searchTerm.toLowerCase()))
                .map(t => (
                  <tr key={t.id} style={{ borderBottom: '1px solid #f1f5f9' }}>
                    <td style={{ padding: '0.8rem 1rem' }}>
                      <div style={{ fontWeight: 800, color: '#1e293b' }}>{t.id}</div>
                      <div style={{ fontSize: '0.72rem', color: '#64748b' }}>Order: #{t.orderId}</div>
                    </td>
                    <td style={{ padding: '0.8rem 1rem', color: '#334155', fontWeight: 600 }}>
                      {t.customerName}
                    </td>
                    <td style={{ padding: '0.8rem 1rem' }}>
                      <span style={{ background: '#eff6ff', color: '#1d4ed8', border: '1px solid #dbeafe', padding: '0.15rem 0.5rem', borderRadius: '3px', fontWeight: 600, fontSize: '0.72rem' }}>
                        {t.method}
                      </span>
                    </td>
                    <td style={{ padding: '0.8rem 1rem', fontWeight: 800, color: '#0f172a' }}>
                      {formatINR(t.amount)}
                    </td>
                    <td style={{ padding: '0.8rem 1rem', color: '#15803d', fontWeight: 700 }}>
                      {formatINR(t.gstAmount)}
                    </td>
                    <td style={{ padding: '0.8rem 1rem' }}>
                      <span style={{
                        padding: '0.2rem 0.55rem',
                        borderRadius: '4px',
                        fontSize: '0.72rem',
                        fontWeight: 700,
                        background: t.status === 'Captured' ? '#dcfce7' : '#fee2e2',
                        color: t.status === 'Captured' ? '#15803d' : '#b91c1c'
                      }}>
                        {t.status}
                      </span>
                    </td>
                    <td style={{ padding: '0.8rem 1rem', textAlign: 'right' }}>
                      <button
                        onClick={() => {
                          const matchedOrder = orders.find(o => o.id === t.orderId);
                          if (matchedOrder) setInvoiceModalOrder(matchedOrder);
                          else addToast?.('Invoice preview generated', 'info');
                        }}
                        style={{
                          background: '#f1f5f9',
                          border: '1px solid #cbd5e1',
                          color: '#334155',
                          padding: '0.35rem 0.65rem',
                          borderRadius: '4px',
                          cursor: 'pointer',
                          display: 'inline-flex',
                          alignItems: 'center',
                          gap: '0.3rem',
                          fontSize: '0.75rem',
                          fontWeight: 600
                        }}
                      >
                        <FileText size={13} />
                        <span>GST Invoice</span>
                      </button>
                    </td>
                  </tr>
                ))}
            </tbody>
          </table>
        </div>
      )}

      {/* Tab 2: GST Monthly Summary */}
      {activeTab === 'gst-reports' && (
        <div style={{ background: '#ffffff', borderRadius: '8px', padding: '1.4rem', border: '1px solid #e0e0e0' }}>
          <h3 style={{ fontSize: '1.1rem', fontWeight: 800, margin: '0 0 1rem', color: '#1e293b' }}>
            Monthly GSTR-1 & B2C Invoicing Summary
          </h3>
          <div style={{ border: '1px solid #e2e8f0', borderRadius: '6px', overflow: 'hidden', marginBottom: '1.2rem' }}>
            <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: '0.84rem' }}>
              <thead style={{ background: '#f8fafc', borderBottom: '1px solid #e2e8f0', color: '#475569' }}>
                <tr>
                  <th style={{ padding: '0.75rem 1rem', textAlign: 'left' }}>Tax Period</th>
                  <th style={{ padding: '0.75rem 1rem', textAlign: 'left' }}>HSN Code</th>
                  <th style={{ padding: '0.75rem 1rem', textAlign: 'right' }}>Total Taxable Value</th>
                  <th style={{ padding: '0.75rem 1rem', textAlign: 'right' }}>CGST (1.5%)</th>
                  <th style={{ padding: '0.75rem 1rem', textAlign: 'right' }}>SGST (1.5%)</th>
                  <th style={{ padding: '0.75rem 1rem', textAlign: 'right' }}>Total Tax</th>
                </tr>
              </thead>
              <tbody>
                <tr>
                  <td style={{ padding: '0.8rem 1rem', fontWeight: 700 }}>October 2026</td>
                  <td style={{ padding: '0.8rem 1rem' }}>7113 (Gold/Silver Jewellery)</td>
                  <td style={{ padding: '0.8rem 1rem', textAlign: 'right', fontWeight: 700 }}>{formatINR(totalCaptured - totalGSTCollected)}</td>
                  <td style={{ padding: '0.8rem 1rem', textAlign: 'right' }}>{formatINR(Math.round(totalGSTCollected / 2))}</td>
                  <td style={{ padding: '0.8rem 1rem', textAlign: 'right' }}>{formatINR(Math.round(totalGSTCollected / 2))}</td>
                  <td style={{ padding: '0.8rem 1rem', textAlign: 'right', fontWeight: 800, color: '#15803d' }}>{formatINR(totalGSTCollected)}</td>
                </tr>
              </tbody>
            </table>
          </div>
          <p style={{ fontSize: '0.82rem', color: '#64748b', margin: 0 }}>
            GST rate of 3% is applicable on precious metal items as per CBIC guidelines. Kitchenware appliances fall under HSN 7323 / 8516.
          </p>
        </div>
      )}

      {/* Tab 3: HSN Configuration */}
      {activeTab === 'hsn-config' && (
        <div style={{ background: '#ffffff', borderRadius: '8px', padding: '1.4rem', border: '1px solid #e0e0e0' }}>
          <h3 style={{ fontSize: '1.1rem', fontWeight: 800, margin: '0 0 1.2rem', color: '#1e293b' }}>
            Tax Classification & Invoice Series Config
          </h3>
          <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '1.2rem' }}>
            <div style={{ border: '1px solid #e2e8f0', padding: '1.1rem', borderRadius: '6px' }}>
              <div style={{ fontWeight: 800, color: '#1e293b', marginBottom: '0.5rem' }}>Jewellery (Gold, Silver, Platinum)</div>
              <div style={{ fontSize: '0.82rem', color: '#475569', marginBottom: '0.4rem' }}>• Standard HSN: <strong>7113</strong></div>
              <div style={{ fontSize: '0.82rem', color: '#475569', marginBottom: '0.4rem' }}>• Statutory GST: <strong>3.00%</strong> (1.5% CGST + 1.5% SGST)</div>
              <div style={{ fontSize: '0.82rem', color: '#475569' }}>• BIS Hallmark: Mandated 6-character HUID</div>
            </div>

            <div style={{ border: '1px solid #e2e8f0', padding: '1.1rem', borderRadius: '6px' }}>
              <div style={{ fontWeight: 800, color: '#1e293b', marginBottom: '0.5rem' }}>Luxury Kitchenware & Appliances</div>
              <div style={{ fontSize: '0.82rem', color: '#475569', marginBottom: '0.4rem' }}>• Stainless Steel Utensils: HSN <strong>7323</strong> (12% GST)</div>
              <div style={{ fontSize: '0.82rem', color: '#475569', marginBottom: '0.4rem' }}>• Electrical Kitchen Appliances: HSN <strong>8516</strong> (18% GST)</div>
              <div style={{ fontSize: '0.82rem', color: '#475569' }}>• Silver Utensils: HSN <strong>7114</strong> (3% GST)</div>
            </div>
          </div>
        </div>
      )}

    </div>
  );
}
